import React, { useState, useCallback } from 'react';
import { 
  Drawer,
  Box, 
  List, 
  ListItem, 
  ListItemButton,
  ListItemText, 
  ListItemIcon,
  IconButton,
  Divider, 
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import ArchiveIcon from '@mui/icons-material/Archive';
import { useClientContext } from '../context/ClientContextProvider';
import { useNavigate } from 'react-router-dom';
import { ClientProfile } from 'shared-lib';

interface PropertyDrawerProps {
  open: boolean;
  onClose: () => void;
}

const PropertyDrawer: React.FC<PropertyDrawerProps> = ({
  open,
  onClose
}) => {
  const { currentProperty, properties, setCurrentProperty, archiveProperty } = useClientContext();
  const navigate = useNavigate();
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [propertyToArchive, setPropertyToArchive] = useState<string | null>(null);

  const handlePropertySelect = useCallback((property: ClientProfile) => {
    setCurrentProperty(property);
    onClose();
    navigate('/');
  }, [setCurrentProperty, onClose, navigate]);

  const handleEdit = useCallback((event: React.MouseEvent, propertyId: string) => {
    event.stopPropagation();
    navigate(`/properties/${propertyId}/edit`);
  }, [navigate]);

  const handleArchiveClick = useCallback((event: React.MouseEvent, propertyId: string) => {
    event.stopPropagation();
    setPropertyToArchive(propertyId);
    setArchiveDialogOpen(true);
  }, []);

  const handleArchiveCancel = useCallback(() => {
    setArchiveDialogOpen(false);
    setPropertyToArchive(null);
  }, []);

  const handleArchiveConfirm = useCallback(async () => {
    if (propertyToArchive) {
      await archiveProperty(propertyToArchive);
      setArchiveDialogOpen(false);
      setPropertyToArchive(null);
    }
  }, [propertyToArchive, archiveProperty]);

  return (
    <>
      <Drawer
        anchor="bottom"
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: { 
            maxHeight: '80vh',
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            padding: '16px 0',
            '& .MuiListItemButton-root': {
              transition: 'background-color 0.3s ease',
              '&.Mui-selected': {
                backgroundColor: 'rgba(0, 171, 155, 0.1)',
                '& .MuiTypography-root': {
                  color: '#00AB9B',
                  fontWeight: 700
                },
                '& .MuiListItemIcon-root': {
                  color: '#00AB9B'
                }
              }
            },
            '& .MuiIconButton-root': {
              color: 'rgba(16, 29, 59, 0.6)',
              '&:hover': {
                backgroundColor: 'rgba(16, 29, 59, 0.04)'
              }
            },
            '& .MuiDivider-root': {
              borderColor: 'rgba(16, 29, 59, 0.2)'
            }
          }
        }}
      >
        <Box sx={{ 
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          height: '100%'
        }}>
          <Typography 
            variant="h6" 
            sx={{ 
              mb: 2, 
              color: '#101D3B',
              fontWeight: 700,
              fontSize: '18px'
            }}
          >
            Select Property
          </Typography>
          
          <List sx={{ 
            flexGrow: 1, 
            overflow: 'auto',
            mb: 2
          }}>
            {properties.map((property) => (
              <ListItem
                key={property.id}
                disablePadding
                secondaryAction={
                  <Box>
                    {/* <IconButton
                      edge="end"
                      aria-label="edit"
                      onClick={(e) => handleEdit(e, property.id)}
                    >
                      <EditIcon />
                    </IconButton> */}
                    <IconButton
                      edge="end"
                      aria-label="archive"
                      onClick={(e) => handleArchiveClick(e, property.id)}
                    >
                      <ArchiveIcon />
                    </IconButton>
                  </Box>
                }
              >
                <ListItemButton
                  selected={property.id === currentProperty?.id}
                  onClick={() => handlePropertySelect(property)}
                >
                  <ListItemIcon>
                    <BusinessIcon />
                  </ListItemIcon>
                  <ListItemText 
                    primary={property.companyName || 'Unnamed Property'} 
                    secondary={property.address ? `${property.address}, ${property.city}` : 'No address'}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
          
          <Divider />
          
          <Box sx={{ mt: 2 }}>
            <Button 
              variant="contained" 
              fullWidth 
              startIcon={<AddIcon />} 
              onClick={() => {
                navigate('/register');
                onClose();
              }}
              sx={{
                backgroundColor: '#00AB9B',
                color: '#FFFFFF',
                textTransform: 'none',
                borderRadius: '5px',
                padding: '10px 20px',
                '&:hover': {
                  backgroundColor: '#00AB9B',
                  filter: 'brightness(0.9)'
                }
              }}
            >
              Add Property
            </Button>
          </Box>
        </Box>
      </Drawer>

      <Dialog
        open={archiveDialogOpen}
        onClose={handleArchiveCancel}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        PaperProps={{
          sx: {
            borderRadius: '16px',
            boxShadow: '0px 8px 32px -4px rgba(16, 29, 59, 0.1)',
            border: '1px solid rgba(16, 29, 59, 0.2)'
          }
        }}
      >
        <DialogTitle 
          id="alert-dialog-title"
          sx={{
            color: '#101D3B',
            fontWeight: 700
          }}
        >
          Archive Property?
        </DialogTitle>
        <DialogContent>
          <DialogContentText 
            id="alert-dialog-description"
            sx={{
              color: 'rgba(16, 29, 59, 0.6)'
            }}
          >
            Are you sure you want to archive this property? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ pb: 3, px: 3 }}>
          <Button 
            onClick={handleArchiveCancel}
            sx={{
              color: '#101D3B',
              borderColor: 'rgba(16, 29, 59, 0.2)',
              borderRadius: '5px',
              textTransform: 'none'
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleArchiveConfirm} 
            sx={{
              backgroundColor: '#f44336',
              color: 'white',
              '&:hover': {
                backgroundColor: '#d32f2f'
              },
              borderRadius: '5px',
              textTransform: 'none'
            }}
            autoFocus
          >
            Archive
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PropertyDrawer; 